import { X } from 'lucide-react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@design-system/Sheet';
import { Button } from '@design-system/Button';
import { cn } from '@/utils/styles';
import { useTrustpilotReviewPopup } from '@/hooks/useTrustpilotReviewPopup';

const CONTACT_SUPPORT_URL = 'https://www.viome.com/contact';
const TRUSTPILOT_REVIEW_IMAGE_URL =
  'https://strapi.azure.viome.com/viome-strapi/uploads/trustpilot_review_fd8ed662c6.png';

export const TrustpilotReviewPopup = () => {
  const { isOpen, isSubmitting, errorMessage, dismiss, openReview } =
    useTrustpilotReviewPopup();

  if (!isOpen) return null;

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <SheetContent
        side="right"
        variant="glass"
        modal
        hideCloseButton
        onInteractOutside={() => dismiss()}
        className={cn(
          'px-6',
          'bg-background-secondary',
          'shadow-[0px_1px_0px_0px_#FFFFFF1A_inset,0px_4px_40px_0px_#44445E29]',
          'max-md:h-auto',
        )}
        data-testid="home-trustpilotReviewPopup"
      >
        <SheetHeader className="px-0 pt-8 pb-0">
          <div className="flex items-start justify-between gap-4">
            <SheetTitle
              className="typography-h2 text-primary font-semibold"
              data-testid="home-trustpilotReviewPopup-title"
            >
              Enjoying Viome?
            </SheetTitle>
            <SheetClose asChild>
              <button
                type="button"
                aria-label="Close"
                className="text-static-secondary hover:text-static-primary transition-colors"
                data-testid="home-trustpilotReviewPopup-close"
              >
                <X className="h-6 w-6" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="px-0 pt-6 flex flex-col gap-4">
          <SheetDescription
            className="px-0 pt-0 typography-body text-secondary"
            data-testid="home-trustpilotReviewPopup-description"
          >
            Your feedback means a lot to us. Let us know what you think!
          </SheetDescription>
          <button
            type="button"
            onClick={openReview}
            disabled={isSubmitting}
            className="w-fit self-center p-0 border-0 bg-transparent cursor-pointer disabled:cursor-not-allowed disabled:opacity-70"
            data-testid="home-trustpilotReviewPopup-imageButton"
            aria-label="Leave a review on Trustpilot"
          >
            <img
              src={TRUSTPILOT_REVIEW_IMAGE_URL}
              alt="Review us on Trustpilot"
              className="w-[15rem] h-auto"
              data-testid="home-trustpilotReviewPopup-image"
            />
          </button>
        </div>

        <div className="pt-6 pb-8 flex flex-col gap-4">
          <p
            className="text-sm text-secondary"
            data-testid="home-trustpilotReviewPopup-disclaimer"
          >
            Feedback is public and cannot be changed. Be fair and factual. If
            there&apos;s an issue, try our{' '}
            <a
              href={CONTACT_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              data-testid="home-trustpilotReviewPopup-supportLink"
            >
              customer support
            </a>
            .
          </p>

          {errorMessage && (
            <p
              className="typography-caption2 text-error"
              role="alert"
              data-testid="home-trustpilotReviewPopup-error"
            >
              {errorMessage}
            </p>
          )}

          <Button
            type="button"
            className="w-full"
            disabled={isSubmitting}
            onClick={openReview}
            data-testid="home-trustpilotReviewPopup-cta"
          >
            {isSubmitting
              ? 'Opening…'
              : errorMessage
                ? 'Try again'
                : 'Leave a review on Trustpilot'}
          </Button>

          <Button
            type="button"
            variant="tertiary"
            className="w-full"
            disabled={isSubmitting}
            onClick={dismiss}
            data-testid="home-trustpilotReviewPopup-dismiss"
          >
            Maybe Later
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
